import { fillTemplate, formatCheckoutTime, formatYMD, getOverdueInvoices, getTerminationDateLimits, isBeforeTermsMinDate, sumAmounts, ymdToLocalDate } from '../core/utils'
import { selectWarehouse } from 'containers/Warehouses/warehousesSlice'
import SoftLimitCalendar from 'components/pickers/SoftLimitCalendar'
import { useHistory, useParams } from 'react-router-dom'
import { SECTION_TYPES } from 'containers/Warehouse'
import ArrowBtn from 'components/buttons/ArrowBtn'
import React, { useEffect, useState } from 'react'
import Checkbox from 'components/inputs/Checkbox'
import useTranslation from 'hooks/useTranslation'
import BackBtn from 'components/buttons/BackBtn'
import { dateToYMDformat } from 'utils/date'
import { displayPrice } from 'utils/string'
import { useReduxSelector } from 'store'

type SettlementChoice = 'schedule' | 'payNow'

interface CalendarProps {
  isLoading: boolean
  isSending: boolean
  response?: payments.Quote
  /** data, dla ktorej pobrano response */
  quoteDate?: string
  selectedData: payments.SelectedData
  selectData: React.Dispatch<React.SetStateAction<payments.SelectedData>>
  error?: string
  /** zapis zakonczenia; settlement=payNow -> ekran platnosci */
  onSubmit: (settlement?: payments.TerminationSettlement) => void
}

const dateStrToObj = (s?: string | null) => s ? ymdToLocalDate(s) : undefined

const Calendar: React.FC<CalendarProps> = ({ isLoading, isSending, response, quoteDate, selectedData, selectData, error, onSubmit }) => {
  const { id } = useParams<warehouse.PageParams>()
  const warehouse = useReduxSelector(state => selectWarehouse(state, id))
  const { rentableType, terminationTerms, resignation, canExtend, fullDateTo } = warehouse
  const [date, setDate] = useState(dateStrToObj(selectedData.date))
  const [termsExpanded, setTermsExpanded] = useState(false)
  const [settlement, setSettlement] = useState<SettlementChoice>('schedule')

  const { push } = useHistory()
  const { t, lang } = useTranslation('warehouse')
  const tc = useTranslation('warehouse.terminationCalendar').t

  const terms: Partial<payments.QuoteTerminationTerms> = { ...terminationTerms, ...response?.terminationTerms }
  const checkoutTime = formatCheckoutTime(response?.flags?.checkoutTime)
  const limits = getTerminationDateLimits(terms, response?.flags?.checkoutTime)
  const selectedYMD = date ? dateToYMDformat(date) : undefined
  const now = new Date()
  const tomorrow = dateToYMDformat(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1))

  // "dowolny termin" - brak okresu wypowiedzenia (nadal obowiazuje godzina wymeldowania, patrz warunki)
  const isAnyTime = response?.horizon?.noticeTemplate === 'day_to_day' || !limits.termsMinDate || limits.termsMinDate <= tomorrow

  /** wycena odpowiada wybranej dacie - dopiero wtedy pokazujemy kwoty i pozwalamy zapisac */
  const isQuoteCurrent = !!response && !!selectedYMD && quoteDate === selectedYMD
  const quote = isQuoteCurrent ? response : undefined

  const overdueInvoices = getOverdueInvoices(response)
  const overdueAmount = sumAmounts(overdueInvoices.map(invoice => invoice.remainingAmount))
  const paidUntil = response?.paidUntil

  const isEarly = isBeforeTermsMinDate(selectedYMD, terms)
  const isPaidAhead = !!(paidUntil && selectedYMD && paidUntil.slice(0, 10) >= selectedYMD)
  const settlementEndDate = quote?.settlementEndDate ?? quote?.horizon?.settlementEndDate ?? limits.termsMinDate

  const dueNowAmount = Number(quote?.amounts?.dueNowGross || 0)
  const dueNowDays = quote?.invoicing?.mode === 'now' ? Number(quote.invoicing.days) || 0 : 0
  const refundAmount = Number(quote?.refund?.paidGross || quote?.amounts?.refundPaidGross || 0)
  const payNowAmount = Number(quote?.amounts?.payNowGross || 0)

  // wybor "Zaplac teraz" ma sens tylko, gdy cos zostaloby zafakturowane PO zapisie (przez workera w kolejnych FV);
  // przy samym to_invoice_now faktura i tak powstaje od razu. payNow jest dostepne tylko dla nowej rezygnacji.
  const showSettlementChoice = !resignation && !!quote?.flags?.canPayNow && payNowAmount > 0 && Number(quote?.amounts?.dueLaterGross || 0) > 0

  /** data juz zatwierdzona przez backend - kalendarz moze byc otwarty tylko bezposrednim linkiem */
  const isDateAccepted = !!resignation?.dateAccepted
  const isBlocked = isDateAccepted || !!quote?.horizon?.invalidReason || (quote?.horizon?.dateBelowMinimum && !terms.canEndBeforeMinDate)

  useEffect(() => {
    if (!showSettlementChoice) setSettlement('schedule')
  }, [showSettlementChoice])

  const termsRules: string[] = []
  // if (terms.infoForClient) termsRules.push(`${tc('termsNoticePeriod')}: ${terms.infoForClient}`)
  if (isAnyTime) {
    termsRules.push(tc('termsAnyTime'))
  } else if (terms.canEndBeforeMinDate) {
    termsRules.push(fillTemplate(tc('termsCanEndEarlier'), { date: formatYMD(limits.termsMinDate) }))
  } else {
    termsRules.push(fillTemplate(tc('termsCannotEndEarlier'), { date: formatYMD(limits.termsMinDate) }))
  }
  if (checkoutTime) termsRules.push(fillTemplate(tc(rentableType === 'parking' ? 'termsCheckoutTimeParking' : 'termsCheckoutTime'), { time: checkoutTime }))
  if (terms.maxDate) termsRules.push(fillTemplate(tc('termsMaxDate'), { date: formatYMD(terms.maxDate) }))
  if (response?.flags?.needPhoto) {
    termsRules.push(response.flags.withoutPhotoCost
      ? fillTemplate(tc('termsPhotoWithCost'), { cost: displayPrice(response.flags.withoutPhotoCost) })
      : tc('termsPhoto'))
  }

  const vacateText = checkoutTime
    ? <> {tc(rentableType === 'parking' ? 'vacateParkingBy' : 'vacateStorageBy')} <b>{checkoutTime}.</b></>
    : null

  return <>
    <div className='title'>
      <div>{t('selectDate')}</div>
      <div>{t('whenDoYouWantToEndYourRent')}</div>
    </div>

    <div className='resignation__main'>
      {/* tresc trzymana razem (gap), przyciski zostaja na dole dzieki space-between w resignation__main */}
      <div className='resignation__content'>
        <div className='resignation__tile --warning resignation__terms'>
          <div className='resignation__terms__main'>
            {isAnyTime
              ? <>{tc('anyTimePrefix')} <b>{tc('anyTimeBold')}</b>.</>
              : <>{tc('nearestEndDate')} <b>{formatYMD(limits.termsMinDate)}</b>.</>}
            {vacateText}
          </div>
          {termsRules.length > 0 && <>
            <button
              type='button'
              className={`resignation__terms__toggle${termsExpanded ? ' --expanded' : ''}`}
              onClick={() => setTermsExpanded(prev => !prev)}>
              <span>{tc(termsExpanded ? 'hideTerms' : 'showTerms')}</span>
              <i className='icon icon--arrow-drop-down s16' />
            </button>
            {termsExpanded && <ul className='resignation__terms__list'>
              {termsRules.map(rule => <li key={rule}>{rule}</li>)}
            </ul>}
          </>}
        </div>

        {overdueInvoices.length > 0
          ? <div className='resignation__tile --error --row'>
            <i className='icon icon--warning2 s16' />
            <span>{tc('unpaidInvoices')} ({displayPrice(overdueAmount)})</span>
            <button
              type='button'
              className='resignation__tile__action'
              onClick={() => push(`/warehouses/${id}/${SECTION_TYPES.invoices}`)}>
              {t('pay2')}
            </button>
          </div>
          : paidUntil
            ? <div className='resignation__tile --success'>
              {tc('paidUntil')} <b>{formatYMD(paidUntil)}</b>
            </div>
            : null}

        <div className='resignation__calendar popup__calendar__container'>
          <SoftLimitCalendar
            value={date}
            locale={lang}
            className='--light'
            minDate={new Date(now.getFullYear(), now.getMonth(), 1)}
            softMinDate={limits.minDate}
            softMaxDate={terms.maxDate}
            getBlockedModal={(reason, blockedDate) => {
              if (reason === 'beforeMin') {
                const isPast = dateToYMDformat(blockedDate) < limits.floor
                return {
                  title: tc(isPast ? 'dateInPastTitle' : 'dateBeforeNoticeTitle'),
                  description: fillTemplate(tc(isPast ? 'dateInPastDesc' : 'dateBeforeNoticeDesc'), { date: formatYMD(limits.minDate) }),
                }
              }
              return {
                title: tc('dateBeyondContractTitle'),
                description: tc('dateBeyondContractDesc'),
                action: canExtend && !resignation?.dateAccepted && (fullDateTo || resignation?.date)
                  ? { label: tc('extendRental'), onClick: () => push(`/warehouses/${id}/${SECTION_TYPES.extension}`) }
                  : undefined,
              }
            }}
            showNeighboringMonth={false}
            onChange={value => {
              const newDate = value as Date
              setDate(newDate)
              selectData(prev => ({ ...prev, date: dateToYMDformat(newDate) }))
            }}
          />
        </div>

        {quote && <div className='resignation__notes'>
          {isEarly && <div className='resignation__tile --warning'>
            {isPaidAhead && quote.horizon?.noticeTemplate !== 'day_to_day'
              ? tc('noRefundForEarlyEnd')
              : fillTemplate(tc('earlyEndCharged'), { date: formatYMD(settlementEndDate) })}
          </div>}

          {dueNowAmount > 0 && <div className='resignation__tile --error'>
            {dueNowDays > 0
              ? fillTemplate(tc('extraChargeDays'), { days: `${dueNowDays} ${tc(dueNowDays === 1 ? 'day' : 'days')}` })
              : tc('extraCharge')} <b>{displayPrice(dueNowAmount)}</b>
          </div>}

          {quote.flags?.hasRefund && refundAmount > 0 && <div className='resignation__tile --success'>
            {tc('refundPrefix')} <b>{displayPrice(refundAmount)}</b> {tc('refundSuffix')}
          </div>}

          {showSettlementChoice && <div className='resignation__settlement'>
            <div className='resignation__settlement__title'>{tc('settlementTitle')}</div>
            <Checkbox
              name='settlement-schedule'
              checked={settlement === 'schedule'}
              changeHandler={() => setSettlement('schedule')}
              label={<>
                {tc('settlementSchedule')}
                {quote.invoicing?.nextInvoiceDate && <span className='resignation__settlement__hint'>
                  {fillTemplate(tc('settlementScheduleNextInvoice'), { date: formatYMD(quote.invoicing.nextInvoiceDate) })}
                </span>}
              </>}
            />
            <Checkbox
              name='settlement-pay-now'
              checked={settlement === 'payNow'}
              changeHandler={() => setSettlement('payNow')}
              label={<>
                {tc('settlementPayNow')} <b>{displayPrice(sumAmounts([payNowAmount, overdueAmount]))}</b>
                {overdueAmount > 0 && ` ${fillTemplate(tc('includingOverdue'), { amount: displayPrice(overdueAmount) })}`}
              </>}
            />
          </div>}
        </div>}

        {quote?.errors?.map((message, idx) => <div key={idx} className='resignation__info --error'>{String(message)}</div>)}
        {isDateAccepted && <div className='resignation__info --error'>{tc('dateAccepted')}</div>}
        {error && <div className='resignation__info --error'>{error}</div>}
      </div>

      <div className='resignation__buttons'>
        <ArrowBtn
          enlarged
          text={tc('finishRental')}
          disabled={!date || !isQuoteCurrent || isBlocked}
          isLoading={isSending || (isLoading && !isQuoteCurrent)}
          action={() => {
            if (!isQuoteCurrent || isSending || isBlocked) return
            onSubmit(showSettlementChoice && settlement === 'payNow' ? 'payNow' : undefined)
          }}
        />
        <BackBtn text={tc('cancel')} />
      </div>
    </div>
  </>
}

export default Calendar
